import type { FC, MouseEventHandler } from 'react';
import React, { useState } from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, SERIF_FONT_FAMILY, mq } from '@clutter/clean';

import type { Location__Benefit, PageQuery } from '@graphql/platform';

import { PageNavigation } from './navigation';
import {
  PAGE_ILLUSTRATIONS,
  PageContent,
  PageSectionText,
  PageSectionTitle,
} from './shared';

const MOBILE_ACTIVE_WIDTH = 287;
const MOBILE_INACTIVE_WIDTH = 172;
const MOBILE_GAP = 20;
const DESKTOP_ACTIVE_WIDTH = 480;
const DESKTOP_INACTIVE_WIDTH = 320;
const DESKTOP_GAP = 24;

const PageBenefitContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;

  transition-property: width, opacity;
  transition-duration: 300ms;
  transition-timing-function: ease-in-out;

  &[aria-current='false'] {
    opacity: 0.5;
  }

  &[aria-current='true'] {
    opacity: 1;
  }

  ${mq({
    gap: ['20px', '24px'],
  })}
`;

const PageBenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const PageBenefitIllustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.cloud};

  & img {
    max-width: 70%;
    object-fit: contain;
  }

  ${mq({
    borderRadius: ['14px', '24px'],
    height: ['144px', '256px'],
  })}

  & img {
    ${mq({
      maxHeight: ['120px', '230px'],
    })}
  }
`;

const PageBenefitsWrapper = styled.div`
  overflow: visible;
  position: relative;
`;

const PageBenefitsList = styled.div<{
  index: number;
  count: number;
}>`
  display: flex;
  transition: transform 300ms ease-in-out;

  ${({ index, count }) =>
    mq({
      gap: [`${MOBILE_GAP}px`, `${DESKTOP_GAP}px`],
      transform: [
        `translateX(-${index * (MOBILE_INACTIVE_WIDTH + MOBILE_GAP)}px)`,
        `translateX(-${index * (DESKTOP_INACTIVE_WIDTH + DESKTOP_GAP)}px)`,
      ],
      width: [
        `${count * (MOBILE_INACTIVE_WIDTH + MOBILE_GAP) - MOBILE_GAP + (MOBILE_ACTIVE_WIDTH - MOBILE_INACTIVE_WIDTH)}px`,
        `${count * (DESKTOP_INACTIVE_WIDTH + DESKTOP_GAP) - DESKTOP_GAP + (DESKTOP_ACTIVE_WIDTH - DESKTOP_INACTIVE_WIDTH)}px`,
      ],
    })}

  & > [aria-current='false'] {
    ${mq({
      width: [`${MOBILE_INACTIVE_WIDTH}px`, `${DESKTOP_INACTIVE_WIDTH}px`],
    })}
  }

  & > [aria-current='true'] {
    ${mq({
      width: [`${MOBILE_ACTIVE_WIDTH}px`, `${DESKTOP_ACTIVE_WIDTH}px`],
    })}
  }
`;

const PageBenefitTitle = styled.h3`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  ${mq({
    fontSize: ['20px', '28px'],
    lineHeight: ['1.4', '40px'],
  })}
`;

const PageBenefit: FC<{
  current: boolean;
  benefit: Pick<
    Location__Benefit,
    'id' | 'title' | 'description' | 'illustration'
  >;
  onSelect(): void;
}> = ({ current, benefit, onSelect }) => {
  const illustration = PAGE_ILLUSTRATIONS[benefit.illustration];

  const onClick: MouseEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect();
  };

  return (
    <PageBenefitContainer onClick={onClick} aria-current={current}>
      <PageBenefitIllustration>
        <img src={illustration} alt={benefit.title} />
      </PageBenefitIllustration>
      <PageBenefitContent>
        <PageBenefitTitle>{benefit.title}</PageBenefitTitle>
        <PageSectionText>{benefit.description}</PageSectionText>
      </PageBenefitContent>
    </PageBenefitContainer>
  );
};

const PageBenefitsContainer = styled.div`
  background: ${COLORS.tealBackground};
  ${mq({
    padding: ['48px 0', '80px 0'],
  })}
`;

const PageBenefitsContent = styled(PageContent)`
  overflow: hidden;
`;

export const PageBenefits: FC<{
  page: PageQuery['page'];
}> = ({ page }) => {
  if (!page) throw new Error('missing page');
  const [index, setIndex] = useState<number>(0);
  const count = page.benefits.length;

  const title = page.benefitsTitle.trim();

  return (
    <PageBenefitsContainer>
      <PageBenefitsContent>
        <Box.Flex flexDirection="column" gap="24px">
          <PageSectionTitle>{title}</PageSectionTitle>
          <PageBenefitsWrapper>
            <PageBenefitsList count={count} index={index}>
              {page.benefits.map((benefit, key) => (
                <PageBenefit
                  key={key}
                  benefit={benefit}
                  current={index === key}
                  onSelect={() => {
                    setIndex(key);
                  }}
                />
              ))}
            </PageBenefitsList>
          </PageBenefitsWrapper>
          <PageNavigation count={count} index={index} onNavigate={setIndex} />
        </Box.Flex>
      </PageBenefitsContent>
    </PageBenefitsContainer>
  );
};
